import {lazy, useCallback, useState, useMemo, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {useSettingsTabs as usePaymentsOnDemandSettingsTabs} from '@paymentsOnDemand/public'
import {useSettingsTabs as usePointsOnPaymentsSettingsTabs} from '@pointsOnPayments/public'

// Creates a lazy loader with a minimum load time of 300 seconds to allow the suspense fallback to show long enough to make sense
const minimumDelayLazyLoader = (filename) =>
  lazy(() =>
    Promise.all([
      import(`./Tabs/${filename}/index.jsx`),
      new Promise((resolve) => {
        setTimeout(resolve, 300)
      }),
    ]).then(([moduleExports]) => moduleExports),
  )

const GeneralTab = minimumDelayLazyLoader('GeneralTab')
const AccountingTab = minimumDelayLazyLoader('AccountingTab')
const SecurityTab = minimumDelayLazyLoader('SecurityTab')

export const useTabs = ({settingsUrls}) => {
  const {t} = useTranslation('settings')

  const {
    isLoading: isPaymentsOnDemandSettingsLoading,
    settingsTabs: paymentsOnDemandSettingsTabs,
  } = usePaymentsOnDemandSettingsTabs()

  const {
    isLoading: isPointsOnPaymentsSettingsLoading,
    settingsTabs: pointsOnPaymentsSettingsTabs,
  } = usePointsOnPaymentsSettingsTabs()

  const tabs = useMemo(() => {
    return [
      {
        tabName: 'general',
        label: t('settings:SettingsScreen.tabs.general'),
        component: <GeneralTab />,
      },
      {
        tabName: 'accounting',
        label: t('settings:SettingsScreen.tabs.accounting'),
        component: <AccountingTab />,
      },
      ...paymentsOnDemandSettingsTabs,
      ...pointsOnPaymentsSettingsTabs,
      {
        tabName: 'password',
        label: t('settings:SettingsScreen.tabs.password'),
        component: <SecurityTab />,
      },
    ]
  }, [paymentsOnDemandSettingsTabs, pointsOnPaymentsSettingsTabs, t])

  // The tabs are still loading if one of the packs have not finished
  const isLoading =
    isPointsOnPaymentsSettingsLoading || isPaymentsOnDemandSettingsLoading

  const currentUrlTabName = (Object.entries(settingsUrls).find(
    (entry) => entry[1] === window.location.origin + window.location.pathname,
  ) || [tabs[0].tabName])[0]

  const currentTabIndex = useMemo(() => {
    if (isLoading) {
      return null
    }

    return tabs.findIndex((tab) => tab.tabName === currentUrlTabName)
  }, [isLoading, currentUrlTabName, tabs])

  const [tabIndex, setTabIndex] = useState()

  useEffect(() => {
    // Once all the tabs are loaded we should set the tab index to
    if (!isLoading) {
      setTabIndex(currentTabIndex === -1 ? 0 : currentTabIndex)
    }
  }, [currentTabIndex, isLoading])

  const currentTabObject = tabs[tabIndex] || tabs[0]

  const changeTabs = useCallback(
    (index) => {
      setTabIndex(index)

      window.history.replaceState(
        {},
        '',
        new URL(settingsUrls[tabs[index].tabName]),
      )
    },
    [settingsUrls, tabs],
  )

  return {
    tabs,
    tabIndex,
    changeTabs,
    currentTabObject,
    isLoading,
  }
}
